import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Componente de input com autocomplete/typeahead.
 * Exibe sugestões enquanto o usuário digita, com debounce e navegação por teclado.
 */
function AutocompleteInput({
  id,
  name,
  value,
  onChange,
  onSelect,
  fetchSuggestions,
  placeholder,
  className,
  disabled,
  required,
  minChars = 2,
  debounceMs = 300,
  renderSuggestion,
  noResultsText = 'Nenhum resultado encontrado',
  loadingText = 'Buscando...',
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasFocus, setHasFocus] = useState(false);
  const debounceTimerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Busca sugestões com debounce (apenas se o campo estiver com foco)
  useEffect(() => {
    if (!hasFocus || value.length < minChars) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchSuggestions(value);
        setSuggestions(results || []);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Erro ao buscar sugestões:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value, hasFocus, minChars, debounceMs, fetchSuggestions]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    onChange(e);
  };

  const handleFocus = () => {
    setHasFocus(true);
  };

  const handleBlur = () => {
    // Pequeno delay para permitir clique em sugestões
    setTimeout(() => {
      setHasFocus(false);
      setShowDropdown(false);
    }, 200);
  };

  const handleSelectSuggestion = (suggestion) => {
    onSelect(suggestion);
    setShowDropdown(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    setHasFocus(false);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const defaultRenderSuggestion = (suggestion) => {
    return suggestion.nome_adolescente || suggestion.name || String(suggestion);
  };

  const renderFunction = renderSuggestion || defaultRenderSuggestion;

  return (
    <div className="autocomplete-wrapper" style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        className={className}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete="off"
      />

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="autocomplete-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1050,
            maxHeight: '250px',
            overflowY: 'auto',
            backgroundColor: 'var(--bs-body-bg)',
            border: '1px solid var(--bs-border-color)',
            borderRadius: '0.375rem',
            marginTop: '2px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
          }}
        >
          {loading && (
            <div
              className="autocomplete-item"
              style={{
                padding: '0.5rem 0.75rem',
                color: 'var(--bs-secondary)',
                fontStyle: 'italic',
              }}
            >
              {loadingText}
            </div>
          )}

          {!loading && suggestions.length === 0 && (
            <div
              className="autocomplete-item"
              style={{
                padding: '0.5rem 0.75rem',
                color: 'var(--bs-secondary)',
                fontStyle: 'italic',
              }}
            >
              {noResultsText}
            </div>
          )}

          {!loading &&
            suggestions.map((suggestion, index) => (
              <div
                key={suggestion.id || index}
                className={`autocomplete-item ${
                  index === selectedIndex ? 'active' : ''
                }`}
                onClick={() => handleSelectSuggestion(suggestion)}
                style={{
                  padding: '0.5rem 0.75rem',
                  cursor: 'pointer',
                  backgroundColor:
                    index === selectedIndex
                      ? 'var(--bs-primary)'
                      : 'transparent',
                  color:
                    index === selectedIndex
                      ? 'var(--bs-white)'
                      : 'var(--bs-body-color)',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {renderFunction(suggestion)}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

AutocompleteInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  fetchSuggestions: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  minChars: PropTypes.number,
  debounceMs: PropTypes.number,
  renderSuggestion: PropTypes.func,
  noResultsText: PropTypes.string,
  loadingText: PropTypes.string,
};

export default AutocompleteInput;
